import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import App from './App';
import './styles/global.css';

/* Native shell bootstrap — status bar style and splash dismissal.
 * No-op in the web/PWA build because `Capacitor.isNativePlatform()`
 * returns false there. */
if (Capacitor.isNativePlatform()) {
  // White content on our dark theme. iOS auto-detection is usually
  // correct, but pinning the style here protects against future
  // iOS versions changing the heuristic.
  StatusBar.setStyle({ style: Style.Light }).catch(() => {
    /* older iOS or no permission — ignore. */
  });
}

// Visible error reporting — iOS Safari otherwise swallows JS errors silently.
function showError(label: string, err: unknown) {
  const root = document.getElementById('root');
  const msg =
    err instanceof Error
      ? `${err.name}: ${err.message}\n\n${err.stack ?? ''}`
      : String(err);
  const html = `
    <div style="
      position: fixed; inset: 0; padding: 20px; overflow: auto;
      background: #1a0a0a; color: #ffb4b4;
      font: 13px/1.5 -apple-system, monospace;
      white-space: pre-wrap; word-break: break-word;
      z-index: 99999;
    ">
      <div style="font-weight:700;font-size:15px;color:#ff6b6b;margin-bottom:10px">
        ${label}
      </div>
      <div>${msg.replace(/[<&]/g, (c) => (c === '<' ? '&lt;' : '&amp;'))}</div>
      <div style="margin-top:14px;color:#888;font-size:11px">
        UA: ${navigator.userAgent}
      </div>
    </div>`;
  if (root) root.innerHTML = html;
  else document.body.insertAdjacentHTML('beforeend', html);
}

window.addEventListener('error', (e) => showError('Uncaught error', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) =>
  showError('Unhandled promise rejection', e.reason),
);

class Boundary extends React.Component<
  { children: React.ReactNode },
  { err: unknown }
> {
  state = { err: null as unknown };
  static getDerivedStateFromError(err: unknown) {
    return { err };
  }
  componentDidCatch(err: unknown) {
    showError('React render error', err);
  }
  render() {
    return this.state.err ? null : this.props.children;
  }
}

try {
  const container = document.getElementById('root');
  if (!container) throw new Error('Root container missing');

  createRoot(container).render(
    <React.StrictMode>
      <Boundary>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </Boundary>
    </React.StrictMode>,
  );

  /* Hide the native splash AFTER the browser has actually painted
   * the first frame. Double-rAF is the cheapest reliable signal: the
   * inner callback runs in the frame *after* the one React committed,
   * so the splash fades into already-visible UI instead of into a
   * blank webview. No-op on web. */
  if (Capacitor.isNativePlatform()) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        SplashScreen.hide({ fadeOutDuration: 220 }).catch(() => {});
      });
    });
  }
} catch (e) {
  showError('Bootstrap error', e);
}
